import { APIMaterialRoot, Material, MaterialColor, MaterialRoot } from './types/catalog-internal';
import { CATMaterialRoot, CfgOptionsRoot, CommonElementsRoot, ElementsRoot } from './types/catalog-types';

export function makeFamilyRoot(family: string, variant: string|undefined) {
    if(variant) {
        return `/${family}.${variant}`
    } else {
        return `/${family}`
    }
}

export function transformMaterialsResponse(response:any, lang: string): CATMaterialRoot {

    const tags = (response as any).tags.map((tag:any) => {
        return {
            ...tag,
            name: tag.name[lang]
        }
    });
    const qualities = (response as any).qualities.filter((quality:any) => {
        return !quality.disabled;
    })
    const assets = (response as any).assets.filter((color:any) => {
        return !color.disabled;
    });

    const builtin = (response as any).builtin.map((b:any) => {
        return {
            id: b.id,
            type: b.type,
            qualityId: b.quality_id,
            assetId: b.asset_id,
            leatherAccent: b.leatheraccent ? b.leatheraccent.toLowerCase() : undefined,   // clean the ""
            description: b[`description_${lang}`]
        }
    });

    const builtinQualities = (response as any).builtinQualities.map((b:any) => {
        return {
            qualityId: b.qualityid,
            description: b[`quality_description_${lang}`]
        }
    });

    return { tags, qualities, assets, builtinQualities, builtin }

}


export function transformServerMaterialsResponse(response: any, lang: string): APIMaterialRoot {

    if(!(response as any).SUCCESS) {
        throw new Error('Error retrieving materials from serve API');
    }
    const colors = (response as any).colors.map((color:any) => {
        return {
            id: color.id,
            description: color[`description_${lang}`],
            category: color.category,
            type: color.type,
            qualityId: color.quality_id,
            assetId: color.asset_id,
            leatherAccent: color.leatheraccent ? color.leatheraccent.toLowerCase() : undefined    // clean ""
        }
    });
    const qualities = (response as any).qualities.map((quality:any) => {
        const color = (response as any).colors.find(
            (c:any) => c.quality_id === quality.qualityid
        );
        const translation = quality[lang.toUpperCase()];
        return {
            qualityId: quality.qualityid,
            description: quality[`quality_description_${lang}`],
            type: color ? color.type : '',
            general: translation.general,
            technical: translation.technical,
            maintenance: translation.maintenance
        }
    }).filter((quality:any) => quality.description);
    return { colors, qualities }

}

let notFoundNotified = false;

export function combineMaterialQueries(api: APIMaterialRoot, catalog: CATMaterialRoot): MaterialRoot {

    const allMaterials = api.qualities.concat(catalog.builtinQualities);
    const notFound: string[] = [];

    const materials: Material[] = allMaterials.map(quality => {
        const catalogEntry = catalog.qualities.find(q => q.qualityId === quality.qualityId);
        if(!catalogEntry) {
            notFound.push(quality.qualityId);
            return null;
        }
        return {
            qualityId: quality.qualityId,
            code: catalogEntry.code,
            mainColor: catalogEntry.mainColor,
            tags: catalogEntry.tags,
            name: quality.description,
            general: quality.general,
            technical: quality.technical,
            maintenance: quality.maintenance
        }
    }).filter(m => m !== null ) as Material[];
    materials.sort((a,b) => a.name.localeCompare(b.name));

    if(!notFoundNotified && notFound.length > 0) {
        console.log(`Catalog entry not found for qualities: ${notFound.join(', ')}`);
        notFoundNotified = true;
    }

    const allColors = api.colors.concat(catalog.builtin);

    const colors: MaterialColor[] = allColors.map(color => {

        const material = materials.find(m => m.qualityId === color.qualityId);
        const asset = catalog.assets.find(a => a.assetId === color.assetId);

        let name = color.description;
        if(material && name.toLowerCase().startsWith(material?.name.toLowerCase()+' ')) {
            name = name.substring(material.name.length).trim();
        }

        return {
            colorId: color.id,
            assetId: color.assetId,
            material: material ? material.code : color.qualityId,
            qualityPath: asset?.material ?? '',
            code: asset?.code ?? '',
            name,
            definition: asset?.definition,
            leatherAccent: color.leatherAccent
        }
    });

    return {
        tags: catalog.tags,
        materials,
        colors
    }
}

export function transformOptionsResponse(response: any, lang: string): CfgOptionsRoot {

    const properties = (response as any).properties;                
    const stages = (response as any).stages.map((stage:any) => {
        return {
            ...stage,
            name: stage.name[lang]
        }                    
    });
    const questionGroups = (response as any).questiongroups.map((questionGroup:any) => {
        return {
            ...questionGroup,
            title: questionGroup.title[lang]
        }        
    });
    const questions = ((response as any).questions ?? []).map((question:any) => {
        const result = { ... question };
        if(question.title) {
            result.title = question.title[lang]
        }
        if(question.description) {
            result.description = question.description[lang]
        }
        if(question.nullable) {
            result.nullable = question.nullable[lang]
        }
        return result;
    });
    const values = ((response as any).questionvalues ?? []).map((value:any) => {
        const result = { ...value }
        if(value.title) {
            result.title = value.title[lang]
        }
        if(value.description) {
            result.description = value.description[lang]
        }
        return result;
    })

    const summaryProperties = ((response as any).summaryProperties ?? ['covering'])
    return { properties, stages, questionGroups, questions, values, summaryProperties }
}

export function transformCommonElementsResponse(response: any, lang: string): ElementsRoot {

    const elements = (response.elements ?? []).map((element:any) => {
        return {
            ...element,
            name: element.name[lang]
        }
    });

    return {
        ...response,
        elements
    };
}

export function transformElementsResponse(response: any, lang: string): ElementsRoot {

    const elements = (response.elements ?? []).map((element:any) => {
        return {
            ...element,
            name: element.name[lang]
        }
    });

    const elementGroups = (response.elementGroups ?? []).map((eg:any) => {
        return {
            ...eg,
            name: eg.name[lang]
        }
    });

    const elementFilter = (response.elementFilter ?? []).map((ef:any) => {
        return {
            ...ef,
            name: ef.name[lang]
        }
    });

    // Wait for elementFilter to be loaded before proceeding
    //console.log('Element Filter Debug:', elementFilter);
    
   

    return {
        ...response,
        compose: response.compose ?? false,
        elements,
        elementFilter,
        elementGroups
    };
}

export function combineElementsQueries(family: ElementsRoot, common: CommonElementsRoot): ElementsRoot {
    return {
        ...family,
        elements: family.elements.concat(common.elements),
        elementParts: family.elementParts.concat(common.elementParts)
    }
}